import { useMemo } from 'react';

import { EMPTY_ARRAY, getCustomerBalanceRowID, useMarketAccountsContext, useObservableValue } from '@talos/kyoko';

import { useBalances } from '@src/hooks/useBalances';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useCustomerBalance, useCustomerMarketAccounts } from '@src/providers';
import type { CustomerBalanceTransactionDrawerState } from '../types';
import { resolveCustomerMarketAccountsForBalanceDrawer } from '../utils';
import { useTransferOptions } from './useTransferOptions';

interface UseCustomerBalanceDataProps {
  drawerState: CustomerBalanceTransactionDrawerState;
}

export function useCustomerBalanceData({ drawerState }: UseCustomerBalanceDataProps) {
  const { disableCustomerMarketAccounts } = useFeatureFlag();
  const { marketAccountsByCounterparty, marketAccountsByName } = useMarketAccountsContext();
  const { customerMarketAccountsByName, getActiveCustomerMarketAccounts } = useCustomerMarketAccounts();

  const { customerBalancesMapObs } = useCustomerBalance();
  const customerBalancesMap = useObservableValue(() => customerBalancesMapObs, [customerBalancesMapObs]);

  const { balancesByCurrencyMarketAccountIDObs, currenciesWithBalanceObs } = useBalances({
    filter: {},
    tag: 'CustomerBalanceTransactionDrawer',
    openStreamWithoutFilter: true,
  });

  const currenciesWithBalance = useObservableValue(() => currenciesWithBalanceObs, [currenciesWithBalanceObs]);

  const balancesByCurrencyMarketAccountID = useObservableValue(
    () => balancesByCurrencyMarketAccountIDObs,
    [balancesByCurrencyMarketAccountIDObs]
  );

  const customerAccountBalance = useMemo(() => {
    if (!drawerState.customer || !drawerState.currency || !drawerState.customerAccount) {
      return undefined;
    }
    return customerBalancesMap?.get(
      getCustomerBalanceRowID({
        Counterparty: drawerState.customer,
        MarketAccount: drawerState.customerAccount,
        // Use Currency for the Customer Balance
        Currency: drawerState.currency,
      })
    );
  }, [drawerState.customer, drawerState.currency, drawerState.customerAccount, customerBalancesMap]);

  const { sourceOptions } = useTransferOptions({ drawerState });

  const sourceOption = useMemo(() => {
    return sourceOptions.find(option => option.marketAccount.Name === drawerState.source);
  }, [drawerState.source, sourceOptions]);
  // Get the balance for the selected source and currency
  const sourceAccountBalance = useMemo(() => {
    if (
      !sourceOption?.marketAccount.MarketAccountID ||
      !drawerState.aliasCurrency ||
      !balancesByCurrencyMarketAccountID
    ) {
      return undefined;
    }

    return (
      balancesByCurrencyMarketAccountID
        // Use Alias Currency for the Source Balance
        .get(drawerState.aliasCurrency)
        ?.get(sourceOption.marketAccount.MarketAccountID)
    );
  }, [sourceOption, drawerState.aliasCurrency, balancesByCurrencyMarketAccountID]);

  const customerMarketAccounts = useMemo(() => {
    return resolveCustomerMarketAccountsForBalanceDrawer({
      customer: drawerState.customer,
      disableCustomerMarketAccounts,
      getActiveCustomerMarketAccounts,
      marketAccountsByCounterparty,
    }).map(ma => ma.Name);
  }, [
    drawerState.customer,
    disableCustomerMarketAccounts,
    getActiveCustomerMarketAccounts,
    marketAccountsByCounterparty,
  ]);

  const customerAccountsByName = disableCustomerMarketAccounts ? marketAccountsByName : customerMarketAccountsByName;

  return {
    customerAccountBalance,
    sourceAccountBalance,
    customerMarketAccounts,
    customerMarketAccountsByName: customerAccountsByName,
    currenciesWithBalance: currenciesWithBalance ?? EMPTY_ARRAY,
    balancesByCurrencyMarketAccountID,
  };
}
